import authService from "@/services/auth.service";
import axios from "axios";

// Get API URL from runtime config (for Dokploy auto-generated domains) or fallback to build-time env
const getApiUrl = (): string => {
  // Priority 1: Runtime config (for Dokploy auto-generated domains)
  if (typeof window !== 'undefined' && (window as any).__APP_CONFIG__?.API_URL !== undefined) {
    const runtimeUrl = (window as any).__APP_CONFIG__.API_URL;
    // Allow empty string for relative URLs, but skip if it's a placeholder
    if (runtimeUrl !== null && !runtimeUrl.includes('{{')) {
      return runtimeUrl; // Can be empty string for relative URLs
    }
  }
  
  // Priority 2: Build-time environment variable (Vite)
  if (import.meta.env.VITE_PUBLIC_API_URL) {
    return import.meta.env.VITE_PUBLIC_API_URL;
  }
  
  // Priority 3: Default fallback (use relative URL for all-in-one deployments)
  return import.meta.env.PROD 
    ? '' // Empty string = relative URL (same domain) - works for all-in-one deployments
    : 'http://localhost:5000'; // Local development backend on port 5000
};

export const PUBLIC_API_URL = getApiUrl();



export const api = axios.create({
  baseURL: PUBLIC_API_URL + "/api",
});

api.interceptors.request.use((config) => {
  const token = authService.getToken();
  if (token) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  const storedOrg = localStorage.getItem("active_org");
  if (storedOrg) {
    try {
      config.headers["x-organization-id"] = JSON.parse(storedOrg);
    } catch {
      // ignore invalid JSON
    }
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error?.response?.status === 401 && !originalRequest._retry) {
      const url = originalRequest.url || "";
      const isAuthEndpoint = url.startsWith("/auth/");
      const responseCode = error?.response?.data?.code;
      const isTokenExpired = responseCode === "TOKEN_EXPIRED" ||
        error?.response?.data?.message?.toLowerCase()?.includes("expired");

      // Token expired: clear everything and redirect to login immediately
      if (isTokenExpired) {
        authService.logout();
        window.location.href = "/login";
        return Promise.reject(new Error("Session expired. Please login again."));
      }

      // Only logout + redirect on auth endpoint failures (current, refresh)
      if (isAuthEndpoint) {
        authService.logout();
        window.location.href = "/login";
        return Promise.reject(new Error("Session expired. Please login again."));
      }

      // For non-auth 401s, try refresh once, but never logout
      originalRequest._retry = true;
      try {
        const data = await authService.refresh_token();
        const access_token = data?.access_token;
        if (access_token) {
          api.defaults.headers["Authorization"] = "Bearer " + access_token;
          originalRequest.headers["Authorization"] = "Bearer " + access_token;
          return api(originalRequest);
        }
      } catch {
        // refresh failed — don't logout, just surface the original error
      }
    }
    return Promise.reject(error);
  }
);
